import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { workoutsAPI, exercisesAPI } from '../../api';
import { SessionComplete } from './SessionComplete';
import {
  Plus, Play, Pause, Check, X, Clock, Dumbbell, Trophy,
  TrendingUp, Calendar, BarChart3, ChevronDown, ChevronRight,
  Flame, Target, Zap, Search, Loader2, Sparkles
} from 'lucide-react';

// ---------------------------------------------------------------------------
// useDebounce — small utility to throttle typeahead search calls.
// Returns the input value after `delay` ms of inactivity.
// ---------------------------------------------------------------------------
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// ExerciseCombobox — typeahead input over /api/exercises?search=...
//
// Why: 3,526 seeded exercises in the catalogue. A native <select> forces
// the user to scroll through hundreds of unrecognisable names and they
// can't find what they did today. The typeahead also surfaces muscle
// group + equipment so labels are disambiguated ("Leg Press (Machine) —
// quadriceps/glutes/calves/hamstrings" vs "Leg Extension", etc.) and the
// user can always type a custom name if the catalogue doesn't have it
// (logs without an exercise_id are allowed by the backend, PR/analytics
// are skipped for those rows).
// ---------------------------------------------------------------------------
function ExerciseCombobox({ value, allExercises, onSelect }) {
  // value: {
  //   exercise_id: string|null,
  //   exercise_name: string,
  //   exercise_meta?: { muscle_groups, equipment, category, is_custom } — set on pick
  // }
  const [query, setQuery] = useState(value?.exercise_name || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debouncedQuery = useDebounce(query, 220);

  // Resolve the currently selected exercise either from the meta that
  // was captured at pick time (preferred — always available) or by
  // looking it up in the in-memory catalogue (fallback for older saved
  // workouts where the meta wasn't captured). The catalogue in
  // allExercises is limited to the first 100 entries (the default
  // exercisesAPI.list() limit), so for a freshly picked exercise the
  // captured meta is the only reliable source.
  const selectedEx = useMemo(() => {
    if (value?.exercise_meta) return value.exercise_meta;
    if (!value?.exercise_id) return null;
    return allExercises.find((e) => e.id === value.exercise_id) || null;
  }, [value?.exercise_id, value?.exercise_meta, allExercises]);
  const isCustom = !!value?.exercise_name && !value?.exercise_id;

  // Local fuzzy match against the in-memory catalogue so search feels
  // instant even before the network round-trip. The server search is
  // still authoritative for ordering + coverage of the 3,526 catalogue.
  // Matches against `name` AND `aliases` (server-seeded for high-value
  // exercises like "barbell row" -> "Bent Over Barbell Row" so users
  // who don't know the catalogue's formal names can find what they did).
  const localMatches = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return allExercises.slice(0, 30);
    const hits = [];
    for (const ex of allExercises) {
      const name = (ex.name || '').toLowerCase();
      const aliases = Array.isArray(ex.aliases) ? ex.aliases : [];
      const nameHit = name.indexOf(q);
      const aliasHit = nameHit >= 0
        ? -1
        : aliases.findIndex((a) => (a || '').toLowerCase().includes(q));
      if (nameHit >= 0) {
        hits.push({ ex, _pos: nameHit, _source: 'name' });
      } else if (aliasHit >= 0) {
        hits.push({ ex, _pos: 100 + aliasHit, _source: 'alias' });
      }
      if (hits.length >= 60) break;
    }
    // Sort: name-prefix match first, then name-substring, then alias match,
    // then earlier position, then alphabetical.
    hits.sort((a, b) => {
      const aNamePrefix = a.ex.name.toLowerCase().startsWith(q) ? 0 : 1;
      const bNamePrefix = b.ex.name.toLowerCase().startsWith(q) ? 0 : 1;
      if (aNamePrefix !== bNamePrefix) return aNamePrefix - bNamePrefix;
      if (a._source !== b._source) return a._source === 'name' ? -1 : 1;
      if (a._pos !== b._pos) return a._pos - b._pos;
      return a.ex.name.localeCompare(b.ex.name);
    });
    return hits.map((h) => h.ex);
  }, [debouncedQuery, allExercises]);

  // Server search (more authoritative, supports synonyms / external catalogue)
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    exercisesAPI
      .list({ search: debouncedQuery.trim(), limit: 30 })
      .then((res) => {
        if (cancelled) return;
        setResults(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Merge: server results first (more authoritative), then any local
  // matches that the server missed (e.g. just-created custom entries).
  const merged = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const ex of results) {
      if (ex && ex.id && !seen.has(ex.id)) {
        seen.add(ex.id);
        out.push(ex);
      }
    }
    for (const ex of localMatches) {
      if (ex && ex.id && !seen.has(ex.id)) {
        seen.add(ex.id);
        out.push(ex);
      }
    }
    return out;
  }, [results, localMatches]);

  // Show the "Use custom name" affordance when the user typed something
  // that isn't an exact match in the catalogue.
  const showCustomOption = useMemo(() => {
    const q = query.trim();
    if (!q) return false;
    if (value?.exercise_name && value.exercise_name.trim() === q) return false;
    const exact = merged.find(
      (ex) => (ex.name || '').toLowerCase() === q.toLowerCase()
    );
    return !exact;
  }, [query, merged, value]);

  const pick = (ex) => {
    // Capture the meta so the trigger can render muscle group + equipment
    // chips even if the exercise isn't in the in-memory catalogue (which
    // is capped at 100 by the default exercisesAPI.list() limit).
    onSelect({
      exercise_id: ex.id,
      exercise_name: ex.name,
      exercise_meta: {
        muscle_groups: ex.muscle_groups || [],
        equipment: ex.equipment || [],
        category: ex.category || null,
        is_custom: false,
      },
    });
    setQuery(ex.name);
    setOpen(false);
  };

  const pickCustom = () => {
    const name = query.trim();
    if (!name) return;
    onSelect({
      exercise_id: null,
      exercise_name: name,
      exercise_meta: {
        muscle_groups: [],
        equipment: [],
        category: null,
        is_custom: true,
      },
    });
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, merged.length - 1 + (showCustomOption ? 1 : 0)));
      setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const totalOptions = merged.length + (showCustomOption ? 1 : 0);
      if (activeIdx < merged.length) {
        pick(merged[activeIdx]);
      } else if (showCustomOption) {
        pickCustom();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef} data-testid="exercise-combobox">
      <div
        className={`flex flex-col gap-1 px-3 py-1.5 border rounded text-sm bg-white ${
          selectedEx || isCustom ? 'border-indigo-200' : 'border-gray-200'
        }`}
      >
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder="Search exercises — try 'leg press', 'squat', 'lunge'…"
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setActiveIdx(0);
              // If the user clears the field, clear the selected exercise too.
              if (!e.target.value) onSelect({ exercise_id: null, exercise_name: '', exercise_meta: null });
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            className="flex-1 min-w-0 outline-none bg-transparent text-sm"
            data-testid="exercise-combobox-input"
            aria-autocomplete="list"
            aria-expanded={open}
          />
          {loading && <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin shrink-0" />}
          {query && !loading && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                onSelect({ exercise_id: null, exercise_name: '', exercise_meta: null });
                inputRef.current?.focus();
              }}
              className="text-gray-400 hover:text-gray-600 shrink-0"
              aria-label="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {/* Inline meta so the selected label is recognisable at a glance.
            The user reported that bare catalogue names like "Bent Over
            Low-Pulley Side Lateral" felt unrecognisable; showing the
            muscle group + equipment inline makes the label self-explanatory
            and removes the "is this even the right exercise?" hesitation. */}
        {(selectedEx || isCustom) && (
          <div
            className="flex flex-wrap gap-1 pl-6 -mt-0.5"
            data-testid="exercise-combobox-meta"
          >
            {isCustom && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                <Sparkles className="w-2.5 h-2.5" />
                custom
              </span>
            )}
            {selectedEx?.muscle_groups?.slice(0, 2).map((mg) => (
              <span
                key={mg}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100"
              >
                {mg}
              </span>
            ))}
            {selectedEx?.equipment?.slice(0, 2).map((eq) => (
              <span
                key={eq}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200"
              >
                {eq}
              </span>
            ))}
            {selectedEx?.category && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-500">
                {selectedEx.category}
              </span>
            )}
          </div>
        )}
      </div>

      {open && (query || merged.length > 0) && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
          data-testid="exercise-combobox-results"
        >
          {merged.length === 0 && !showCustomOption && !loading && (
            <div className="p-3 text-sm text-gray-500">
              No matches in the catalogue. You can type a custom name below.
            </div>
          )}
          {merged.map((ex, idx) => (
            <button
              key={ex.id || `ex-${idx}`}
              type="button"
              onClick={() => pick(ex)}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                idx === activeIdx ? 'bg-indigo-50' : 'hover:bg-gray-50'
              }`}
              data-testid={`exercise-option-${ex.id}`}
            >
              <Dumbbell className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="font-medium text-gray-900 text-sm flex-1 min-w-0 truncate">
                {ex.name}
              </span>
              {ex.muscle_groups?.length > 0 && (
                <span className="text-xs text-gray-500 shrink-0 hidden sm:inline">
                  {ex.muscle_groups.slice(0, 2).join(' / ')}
                </span>
              )}
              {ex.equipment?.length > 0 && (
                <span className="text-xs text-gray-400 shrink-0 hidden md:inline">
                  ({ex.equipment[0]})
                </span>
              )}
            </button>
          ))}
          {showCustomOption && (
            <button
              type="button"
              onClick={pickCustom}
              onMouseEnter={() => setActiveIdx(merged.length)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 border-t border-gray-100 ${
                activeIdx === merged.length ? 'bg-indigo-50' : 'hover:bg-gray-50'
              }`}
              data-testid="exercise-combobox-custom"
            >
              <Plus className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
              <span className="text-sm text-gray-900 flex-1 min-w-0 truncate">
                Use custom name: <span className="font-semibold">"{query.trim()}"</span>
              </span>
              <span className="text-xs text-gray-400 shrink-0">
                log without catalogue entry
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const MOOD_OPTIONS = [
  { value: 'great', label: 'Great 💪', color: 'bg-green-100 text-green-700' },
  { value: 'good', label: 'Good 👍', color: 'bg-blue-100 text-blue-700' },
  { value: 'okay', label: 'Okay 😐', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'tired', label: 'Tired 😓', color: 'bg-orange-100 text-orange-700' },
  { value: 'exhausted', label: 'Exhausted 😵', color: 'bg-red-100 text-red-700' },
];

export function WorkoutLogging({ userRole }) {
  const [activeTab, setActiveTab] = useState('log'); // 'log', 'history', 'stats'
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogForm, setShowLogForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [celebrationLog, setCelebrationLog] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, logsRes, summaryRes] = await Promise.all([
        workoutsAPI.listTemplates(),
        workoutsAPI.getWorkoutLogs({ days: 30, limit: 20 }),
        workoutsAPI.getWorkoutSummary(30)
      ]);
      setTemplates(templatesRes.data);
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartWorkout = (template) => {
    setSelectedTemplate(template);
    setShowLogForm(true);
  };

  const handleStartFreeform = () => {
    setSelectedTemplate(null);
    setShowLogForm(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workout-logging">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workout Log</h1>
          <p className="text-gray-600">Track your workouts and see your progress</p>
        </div>
        <button
          onClick={handleStartFreeform}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          data-testid="start-workout-btn"
        >
          <Play className="w-5 h-5" />
          Start Workout
        </button>
      </div>

      {/* Stats Summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard
            icon={Calendar}
            label="This Week"
            value={summary.workouts_this_week}
            subtext="workouts"
            color="indigo"
          />
          <StatCard
            icon={Clock}
            label="Total Time"
            value={Math.round(summary.total_duration_minutes / 60)}
            subtext="hours"
            color="emerald"
          />
          <StatCard
            icon={Dumbbell}
            label="Total Volume"
            value={formatVolume(summary.total_volume)}
            subtext=""
            color="amber"
          />
          <StatCard
            icon={Flame}
            label="Avg Duration"
            value={Math.round(summary.avg_workout_duration)}
            subtext="min"
            color="rose"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { id: 'log', label: 'Start Workout' },
          { id: 'history', label: 'History' },
          { id: 'stats', label: 'Statistics' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === tab.id
                ? 'bg-white shadow text-indigo-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'log' && (
        <div className="space-y-6">
          {/* Quick Start from Template */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Start from Template</h2>
            {templates.length === 0 ? (
              <p className="text-gray-500 text-sm">No templates available. Create one first!</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.slice(0, 6).map(template => (
                  <TemplateQuickStart
                    key={template.id}
                    template={template}
                    onStart={() => handleStartWorkout(template)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Recent Workouts Preview */}
          {logs.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Workouts</h2>
              <div className="space-y-3">
                {logs.slice(0, 3).map(log => (
                  <WorkoutLogCard
                    key={log.id}
                    log={log}
                    compact
                    onClick={() => { setSelectedLog(log); setActiveTab('history'); }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <WorkoutHistory 
          logs={logs} 
          selectedLog={selectedLog}
          onSelectLog={setSelectedLog}
          onRefresh={fetchData}
        />
      )}

      {activeTab === 'stats' && summary && (
        <WorkoutStats summary={summary} />
      )}

      {/* Workout Log Form Modal */}
      {showLogForm && (
        <WorkoutLogForm
          template={selectedTemplate}
          onClose={() => { setShowLogForm(false); setSelectedTemplate(null); }}
          onSave={(log) => {
            setShowLogForm(false);
            setSelectedTemplate(null);
            setCelebrationLog(log);
          }}
        />
      )}

      {/* Session complete celebration — the "wow" moment */}
      {celebrationLog && (
        <SessionComplete
          log={celebrationLog}
          onClose={() => { setCelebrationLog(null); fetchData(); }}
          onLogAnother={() => { setCelebrationLog(null); setShowLogForm(true); setSelectedTemplate(null); }}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subtext, color }) {
  const colors = {
    indigo: 'from-indigo-500 to-indigo-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    rose: 'from-rose-500 to-rose-600',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-4 text-white`}>
      <Icon className="w-6 h-6 mb-2 opacity-80" />
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm opacity-90">{label} {subtext}</p>
    </div>
  );
}

function TemplateQuickStart({ template, onStart }) {
  return (
    <div className="p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-sm transition">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="font-medium text-gray-900">{template.name}</h3>
          <p className="text-sm text-gray-500">
            {template.exercises.length} exercises • {template.estimated_duration_minutes} min
          </p>
        </div>
        <span className={`px-2 py-0.5 text-xs rounded-full ${
          template.difficulty === 'beginner' ? 'bg-green-100 text-green-700' :
          template.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
          'bg-red-100 text-red-700'
        }`}>
          {template.difficulty}
        </span>
      </div>
      <button
        onClick={onStart}
        className="w-full mt-3 px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition flex items-center justify-center gap-2"
        data-testid={`start-template-${template.id}`}
      >
        <Play className="w-4 h-4" />
        Start This Workout
      </button>
    </div>
  );
}

function WorkoutLogCard({ log, compact, onClick }) {
  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      className={`p-4 bg-gray-50 rounded-lg ${onClick ? 'cursor-pointer hover:bg-gray-100' : ''} transition`}
      onClick={onClick}
      data-testid={`workout-log-${log.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900">{log.workout_name}</h3>
            {log.prs_achieved > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                <Trophy className="w-3 h-3" />
                {log.prs_achieved} PR{log.prs_achieved > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(log.started_at)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {log.duration_minutes} min
            </span>
            {!compact && (
              <>
                <span className="flex items-center gap-1">
                  <Dumbbell className="w-4 h-4" />
                  {log.total_sets} sets
                </span>
                <span className="flex items-center gap-1">
                  <Target className="w-4 h-4" />
                  {formatVolume(log.total_volume)}
                </span>
              </>
            )}
          </div>
        </div>
        {onClick && <ChevronRight className="w-5 h-5 text-gray-400" />}
      </div>
    </div>
  );
}

function WorkoutHistory({ logs, selectedLog, onSelectLog, onRefresh }) {
  const [comparison, setComparison] = useState(null);
  const [showDelete, setShowDelete] = useState(null);

  useEffect(() => {
    if (selectedLog) {
      workoutsAPI.getPlannedVsActual(selectedLog.id)
        .then(res => setComparison(res.data))
        .catch(() => setComparison(null));
    }
  }, [selectedLog]);

  const handleDelete = async (logId) => {
    try {
      await workoutsAPI.deleteWorkoutLog(logId);
      onRefresh();
      if (selectedLog?.id === logId) onSelectLog(null);
    } catch (error) {
      console.error('Error deleting log:', error);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Log List */}
      <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-100 p-4 max-h-[600px] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Workout History</h2>
        {logs.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-8">No workouts logged yet</p>
        ) : (
          <div className="space-y-2">
            {logs.map(log => (
              <div
                key={log.id}
                className={`p-3 rounded-lg cursor-pointer transition ${
                  selectedLog?.id === log.id
                    ? 'bg-indigo-50 border border-indigo-200'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => onSelectLog(log)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{log.workout_name}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(log.started_at).toLocaleDateString()}
                    </p>
                  </div>
                  {log.prs_achieved > 0 && (
                    <Trophy className="w-4 h-4 text-yellow-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Log Detail */}
      <div className="lg:col-span-2">
        {selectedLog ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedLog.workout_name}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(selectedLog.started_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <button
                onClick={() => setShowDelete(selectedLog.id)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Delete
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{selectedLog.duration_minutes}</p>
                <p className="text-xs text-gray-500">minutes</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{selectedLog.total_sets}</p>
                <p className="text-xs text-gray-500">sets</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{selectedLog.total_reps}</p>
                <p className="text-xs text-gray-500">reps</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{formatVolume(selectedLog.total_volume)}</p>
                <p className="text-xs text-gray-500">volume</p>
              </div>
            </div>

            {/* Mood & Energy */}
            {(selectedLog.mood || selectedLog.energy_level) && (
              <div className="flex gap-4 mb-6">
                {selectedLog.mood && (
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    MOOD_OPTIONS.find(m => m.value === selectedLog.mood)?.color || 'bg-gray-100'
                  }`}>
                    {MOOD_OPTIONS.find(m => m.value === selectedLog.mood)?.label}
                  </span>
                )}
                {selectedLog.energy_level && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                    Energy: {selectedLog.energy_level}/5
                  </span>
                )}
              </div>
            )}

            {/* Exercises */}
            <h3 className="font-semibold text-gray-900 mb-3">Exercises</h3>
            <div className="space-y-3">
              {selectedLog.exercises.map((ex, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        {ex.exercise_name || 'Unknown Exercise'}
                        {ex.pr_achieved && (
                          <Trophy className="w-4 h-4 text-yellow-500" />
                        )}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {ex.actual_sets} sets: {ex.actual_reps.join(' / ')} reps
                      </p>
                      {ex.actual_weight?.length > 0 && (
                        <p className="text-sm text-gray-500">
                          Weight: {ex.actual_weight.join(' / ')}
                        </p>
                      )}
                    </div>
                    {comparison && (
                      <ComparisonBadge comparison={comparison.find(c => c.exercise_name === ex.exercise_name)} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {selectedLog.notes && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Notes</p>
                <p className="text-sm text-gray-600 mt-1">{selectedLog.notes}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-center h-64">
            <p className="text-gray-500">Select a workout to view details</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      {showDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm">
            <h3 className="text-lg font-semibold mb-2">Delete Workout?</h3>
            <p className="text-gray-600 text-sm mb-4">This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDelete(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => { handleDelete(showDelete); setShowDelete(null); }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ComparisonBadge({ comparison }) {
  if (!comparison) return null;

  const exceeded = comparison.exceeded;
  const rate = comparison.completion_rate;

  if (rate >= 100) {
    return (
      <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
        <TrendingUp className="w-3 h-3" />
        {exceeded ? 'Exceeded!' : 'Complete'}
      </span>
    );
  }
  return (
    <span className="text-xs text-gray-500">{rate}% of plan</span>
  );
}

function WorkoutStats({ summary }) {
  return (
    <div className="space-y-6">
      {/* Weekly Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Monthly Overview</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg text-center">
            <p className="text-3xl font-bold text-indigo-600">{summary.total_workouts}</p>
            <p className="text-sm text-gray-500">Total Workouts</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg text-center">
            <p className="text-3xl font-bold text-emerald-600">{summary.total_sets}</p>
            <p className="text-sm text-gray-500">Total Sets</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg text-center">
            <p className="text-3xl font-bold text-amber-600">{summary.total_reps}</p>
            <p className="text-sm text-gray-500">Total Reps</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg text-center">
            <p className="text-3xl font-bold text-rose-600">{formatVolume(summary.total_volume)}</p>
            <p className="text-sm text-gray-500">Total Volume</p>
          </div>
        </div>
      </div>

      {/* Favorite Exercises */}
      {summary.favorite_exercises?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Most Performed Exercises</h2>
          <div className="space-y-3">
            {summary.favorite_exercises.map((ex, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <span className="w-8 h-8 flex items-center justify-center bg-indigo-100 text-indigo-700 rounded-full font-bold text-sm">
                  {idx + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{ex.name}</p>
                  <p className="text-sm text-gray-500">{ex.count} times • {ex.total_sets} sets</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workout Frequency Calendar */}
      {summary.workout_frequency?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Calendar</h2>
          <div className="flex gap-1 flex-wrap">
            {generateCalendarDays(summary.workout_frequency)}
          </div>
        </div>
      )}
    </div>
  );
}

function generateCalendarDays(frequency) {
  const days = [];
  const today = new Date();
  const frequencyMap = new Map(frequency.map(f => [f.date, f.count]));

  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const count = frequencyMap.get(dateStr) || 0;

    days.push(
      <div
        key={dateStr}
        className={`w-8 h-8 rounded ${
          count === 0 ? 'bg-gray-100' :
          count === 1 ? 'bg-indigo-200' :
          count === 2 ? 'bg-indigo-400' :
          'bg-indigo-600'
        }`}
        title={`${dateStr}: ${count} workout${count !== 1 ? 's' : ''}`}
      />
    );
  }
  return days;
}

function WorkoutLogForm({ template, onClose, onSave }) {
  const [exercises, setExercises] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [formData, setFormData] = useState({
    workout_name: template?.name || '',
    template_id: template?.id || null,
    started_at: new Date().toISOString().slice(0, 16),
    notes: '',
    energy_level: 3,
    mood: 'good',
    exercises: template?.exercises?.map(ex => ({
      exercise_id: ex.exercise_id,
      exercise_name: ex.exercise_name,
      exercise_meta: null,  // populated on first edit via ExerciseCombobox pick
      planned_sets: ex.sets,
      planned_reps: ex.reps,
      planned_weight: ex.weight || '',
      actual_sets: ex.sets,
      actual_reps: Array(ex.sets).fill(''),
      actual_weight: Array(ex.sets).fill(ex.weight || ''),
      notes: ''
    })) || []
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    exercisesAPI.list().then(res => setAllExercises(res.data));
  }, []);

  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => setTimer(t => t + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const addExercise = () => {
    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, {
        exercise_id: '',
        exercise_name: '',
        exercise_meta: null,  // populated by the ExerciseCombobox on pick
        planned_sets: 3,
        planned_reps: '10',
        planned_weight: '',
        actual_sets: 3,
        actual_reps: ['', '', ''],
        actual_weight: ['', '', ''],
        notes: ''
      }]
    }));
  };

  const updateExercise = (idx, field, value) => {
    setFormData(prev => {
      const newExercises = [...prev.exercises];
      newExercises[idx] = { ...newExercises[idx], [field]: value };
      
      // If changing sets count, adjust arrays
      if (field === 'actual_sets') {
        const newSets = parseInt(value) || 1;
        newExercises[idx].actual_reps = Array(newSets).fill('').map((_, i) => 
          newExercises[idx].actual_reps[i] || ''
        );
        newExercises[idx].actual_weight = Array(newSets).fill('').map((_, i) => 
          newExercises[idx].actual_weight[i] || ''
        );
      }
      
      // If selecting exercise, update name
      if (field === 'exercise_id') {
        const ex = allExercises.find(e => e.id === value);
        if (ex) newExercises[idx].exercise_name = ex.name;
      }
      
      return { ...prev, exercises: newExercises };
    });
  };

  const updateSetValue = (exIdx, setIdx, field, value) => {
    setFormData(prev => {
      const newExercises = [...prev.exercises];
      newExercises[exIdx][field][setIdx] = value;
      return { ...prev, exercises: newExercises };
    });
  };

  const removeExercise = (idx) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== idx)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.workout_name.trim()) {
      setError('Please enter a workout name');
      return;
    }
    if (formData.exercises.length === 0) {
      setError('Add at least one exercise');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        template_id: formData.template_id,
        workout_name: formData.workout_name,
        started_at: new Date(formData.started_at).toISOString(),
        completed_at: new Date().toISOString(),
        duration_minutes: Math.ceil(timer / 60),
        notes: formData.notes,
        energy_level: formData.energy_level,
        mood: formData.mood,
        exercises: formData.exercises.map(ex => ({
          exercise_id: ex.exercise_id,
          exercise_name: ex.exercise_name,
          planned_sets: ex.planned_sets,
          planned_reps: ex.planned_reps,
          planned_weight: ex.planned_weight,
          actual_sets: ex.actual_sets,
          actual_reps: ex.actual_reps.map(r => parseInt(r) || 0),
          actual_weight: ex.actual_weight,
          notes: ex.notes
        }))
      };

      await workoutsAPI.logWorkout(payload);
      // Pass the saved log up so the parent can show the celebration.
      onSave({ ...payload, exercises: payload.exercises });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save workout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="workout-log-form">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full my-8">
        <div className="sticky top-0 bg-white border-b border-gray-100 p-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Log Workout</h2>
              <p className="text-sm text-gray-500">{template ? `From: ${template.name}` : 'Freeform Workout'}</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Timer */}
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
                <Clock className="w-5 h-5 text-gray-500" />
                <span className="font-mono text-lg font-semibold">{formatTime(timer)}</span>
                <button
                  type="button"
                  onClick={() => setIsRunning(!isRunning)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Workout Name *</label>
              <input
                type="text"
                value={formData.workout_name}
                onChange={(e) => setFormData(prev => ({ ...prev, workout_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                data-testid="workout-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Energy</label>
                <select
                  value={formData.energy_level}
                  onChange={(e) => setFormData(prev => ({ ...prev, energy_level: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n} - {['Low', 'Below Avg', 'Average', 'Good', 'Great'][n-1]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mood</label>
                <select
                  value={formData.mood}
                  onChange={(e) => setFormData(prev => ({ ...prev, mood: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  {MOOD_OPTIONS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Exercises */}
          <div>
            {/* Sticky sub-header so the "Add Exercise" button is never
                hidden behind the modal's main timer header once the user
                has logged 2+ exercises and the form scrolls. */}
            <div className="sticky top-[72px] z-10 -mx-6 px-6 py-2 bg-white border-b border-gray-100 flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-gray-700">
                Exercises
                {formData.exercises.length > 0 && (
                  <span className="ml-2 text-xs text-gray-500 font-normal">
                    ({formData.exercises.length})
                  </span>
                )}
              </label>
              <button
                type="button"
                onClick={addExercise}
                className="flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                data-testid="add-exercise-btn"
              >
                <Plus className="w-4 h-4" />
                Add Exercise
              </button>
            </div>

            <div className="space-y-4">
              {formData.exercises.map((ex, exIdx) => (
                <ExerciseLogInput
                  key={exIdx}
                  exercise={ex}
                  index={exIdx}
                  allExercises={allExercises}
                  onUpdate={(field, value) => updateExercise(exIdx, field, value)}
                  onUpdateSet={(setIdx, field, value) => updateSetValue(exIdx, setIdx, field, value)}
                  onRemove={() => removeExercise(exIdx)}
                />
              ))}
            </div>

            {formData.exercises.length === 0 && (
              <div className="text-center py-8 px-4 border border-dashed border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">No exercises added yet.</p>
                <p className="text-xs text-gray-500 mt-1">
                  Click <span className="font-semibold">Add Exercise</span> above — then type to search the catalogue
                  (e.g. "leg press", "squat") or enter a custom name if your exercise isn't listed.
                </p>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              placeholder="How did the workout feel?"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="finish-workout-btn"
            >
              <Check className="w-5 h-5" />
              {saving ? 'Saving...' : 'Finish Workout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ExerciseLogInput({ exercise, index, allExercises, onUpdate, onUpdateSet, onRemove }) {
  const [expanded, setExpanded] = useState(true);

  // Combobox owns its own internal query state and notifies us via
  // onUpdate('exercise_id' | 'exercise_name', ...). The old native
  // <select> couldn't search 3,500+ exercises and forced a click-to-
  // expand → scroll → click dance, which is exactly what the user
  // reported ("mora da se šalta i skroluje").
  return (
    <div className="border border-gray-200 rounded-lg overflow-visible">
      <div
        className="flex items-center gap-3 p-3 bg-gray-50 cursor-pointer rounded-lg"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="w-6 h-6 flex items-center justify-center bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium shrink-0">
          {index + 1}
        </span>
        <div
          className="flex-1 min-w-0"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ExerciseCombobox
            value={{
              exercise_id: exercise.exercise_id,
              exercise_name: exercise.exercise_name,
              exercise_meta: exercise.exercise_meta,
            }}
            allExercises={allExercises}
            onSelect={(picked) => {
              onUpdate('exercise_id', picked.exercise_id || '');
              onUpdate('exercise_name', picked.exercise_name || '');
              onUpdate('exercise_meta', picked.exercise_meta || null);
            }}
          />
        </div>
        <span className="text-sm text-gray-500 shrink-0 hidden sm:inline">
          {exercise.actual_sets} sets
        </span>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition shrink-0 ${expanded ? 'rotate-180' : ''}`} />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="p-1 text-gray-400 hover:text-red-500 shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="p-4 space-y-3">
          {/* Planned vs Actual Header */}
          <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 font-medium">
            <div className="col-span-1">Set</div>
            <div className="col-span-2">Planned</div>
            <div className="col-span-4">Actual Reps</div>
            <div className="col-span-4">Actual Weight</div>
            <div className="col-span-1"></div>
          </div>

          {/* Sets */}
          {Array.from({ length: exercise.actual_sets }, (_, setIdx) => (
            <div key={setIdx} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-1 text-sm font-medium text-gray-500">{setIdx + 1}</div>
              <div className="col-span-2 text-sm text-gray-400">
                {exercise.planned_reps} {exercise.planned_weight && `@ ${exercise.planned_weight}`}
              </div>
              <div className="col-span-4">
                <input
                  type="number"
                  value={exercise.actual_reps[setIdx]}
                  onChange={(e) => onUpdateSet(setIdx, 'actual_reps', e.target.value)}
                  placeholder="Reps"
                  className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                />
              </div>
              <div className="col-span-4">
                <input
                  type="text"
                  value={exercise.actual_weight[setIdx]}
                  onChange={(e) => onUpdateSet(setIdx, 'actual_weight', e.target.value)}
                  placeholder="e.g., 50kg"
                  className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                />
              </div>
              <div className="col-span-1"></div>
            </div>
          ))}

          {/* Add/Remove Sets */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => onUpdate('actual_sets', Math.max(1, exercise.actual_sets - 1))}
              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
            >
              - Set
            </button>
            <button
              type="button"
              onClick={() => onUpdate('actual_sets', exercise.actual_sets + 1)}
              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
            >
              + Set
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatVolume(volume) {
  if (volume >= 1000000) return `${(volume / 1000000).toFixed(1)}M`;
  if (volume >= 1000) return `${(volume / 1000).toFixed(1)}K`;
  return Math.round(volume).toString();
}

export default WorkoutLogging;
